/**
 * Professional Loan Insert API Service
 * Handles professional loan application submission (Doctors, CAs, Lawyers)
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/profLoanInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { PDP_DETAILS, PROF_EVENT, PROF_PROD_NAME } from "./constants";
import { logErrorToNewRelic, logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState, commonInsertParameters } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { deleteFromCartApi } from "./cart.service";
import { API_RESPONSE_CODES, OPERATION_IDS } from "./constants";
import { IProfInsertApiRequest, IProfInsertApiResponse } from "../types/profInsertTypes";

/**
 * Insert professional loan application
 *
 * @param data - Professional loan application request data
 * @returns Promise with professional loan insert response
 *
 * @example
 * ```typescript
 * const response = await profLoanInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "PROF_LOAN_DOCTOR",
 *   profession: "Doctor",
 *   subProfession: "General Practitioner",
 *   loanAmt: "1000000"
 * });
 * ```
 */
export const profLoanInsert = async (
  data: IProfInsertApiRequest
): Promise<IProfInsertApiResponse> => {
  try {
    if (data.appId) {
      await deleteFromCartApi({ appId: data.appId, mobileNo: data.mobileNo, channel: "3in1" });
    }
  } catch (error) {
    logErrorToNewRelic(error as string);
  }

  try {
    const response = (await request({
      operationId: OPERATION_IDS.profLoanInsert,
      requestData: {
        ...commonInsertParameters({
          appDtls: {
            custId: data.custId,
            profession: data.profession,
            subProfession: data.subProfession,
          },
        }),
        appVersion: data.appVersion ?? "v4",
        prodName: PROF_PROD_NAME,
        eventCode: data.eventCode ?? PROF_EVENT,
        eventName: PDP_DETAILS,
        ...data,
      },
    })) as IProfInsertApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic("PROF_LOAN_INSERT_ERROR", {
      component: "Prof Loan Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as IProfInsertApiResponse;
  }
};
